import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react'
import * as Sentry from '@sentry/react'
import React, { Component, ErrorInfo, ReactNode } from 'react'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Error boundary that displays errors in a modal instead of replacing the entire page.
 *
 * Features:
 * - Shows errors in a modal overlay instead of the default white error page
 * - Displays error details only in development mode
 * - Integrates with Sentry for error reporting
 * - Provides refresh and dismiss options
 *
 * This component is wrapped around the entire app in _app.tsx
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    Sentry.captureException(error, { contexts: { react: { errorInfo } } })
    this.setState({ error, errorInfo })
  }

  handleRefresh = () => {
    window.location.reload()
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorModal
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRefresh={this.handleRefresh}
        />
      )
    }

    return this.props.children
  }
}

export interface ErrorModalProps {
  error: Error | null
  errorInfo: ErrorInfo | null
  onRefresh: () => void
}

/**
 * Modal component for displaying errors in a user-friendly way.
 * Used by both ErrorBoundary and Next.js _error.tsx page.
 */
export function ErrorModal({ error, errorInfo, onRefresh }: ErrorModalProps) {
  const toast = useToast()

  const errorDetails = error
    ? `${error.toString()}\n\n${errorInfo?.componentStack || ''}`
    : 'An unexpected error occurred'

  const handleCopyError = async () => {
    try {
      await navigator.clipboard.writeText(errorDetails)
      toast({
        title: 'Error text copied to clipboard',
        status: 'success',
        duration: 2000,
      })
    } catch (err) {
      toast({
        title: 'Failed to copy error text',
        status: 'error',
        duration: 3000,
      })
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      size="lg"
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Something went wrong</ModalHeader>
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              We're sorry, but something unexpected happened. You can copy the error details below
              for support or try refreshing the page.
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleCopyError}>
            Copy error text
          </Button>
          <Button variant="primary" onClick={onRefresh}>
            Refresh Page
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
